import { DifficultyChip, type DifficultyChipProps } from '@exam-management/components';
import {
  Checkbox,
  Chip,
  Flex,
  FormCheckbox,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Text,
} from '@shared/components';
import { TableHeader } from '@shared/table-generator';
import { useFormContext } from 'react-hook-form';

import { QuestionNotFoundOverlay } from '../question-not-found-overlay';
import { QuestionUsageStatus, type QuestionUsageStatusProps } from '../question-usage-status';

import { itemSetQuestionChangeTableHeaders } from './item-set-question-change-table-headers';

interface FormItemSetQuestionChangeTableRows {
  id: string;
  name: string;
  questionGroupUsage: boolean;
  question: {
    qId: string;
    difficulty: DifficultyChipProps['type'];
    calculated: boolean;
    questionStatus: QuestionUsageStatusProps;
    action: React.ReactNode;
  }[];
}

export interface FormItemSetQuestionChangeTableProps {
  name: string;
  rows: FormItemSetQuestionChangeTableRows[];
  loading?: boolean;
  total: number;
  slots?: {
    noRowsOverlay?: React.ReactNode;
  };
}

export default function FormItemSetQuestionChangeTable(
  props: Readonly<FormItemSetQuestionChangeTableProps>,
) {
  const { name, rows, slots, total, loading: isLoading = false } = props;
  const formContext = useFormContext();
  const { watch } = formContext;
  const value = watch(name);

  const overlayComponent = isLoading ? (
    <Loading sx={{ m: (theme) => theme.spacing(6) }} />
  ) : (
    (slots?.noRowsOverlay ?? <QuestionNotFoundOverlay />)
  );

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        borderRadius: theme.shape.rounded(2),
        border: `1px solid ${theme.palette.text.primary}`,
        overflow: 'hidden',
      })}
    >
      <TableContainer>
        <Table>
          <TableHeader
            columns={itemSetQuestionChangeTableHeaders}
            slotProps={{
              tableHeaderRow: {
                sx: (theme) => ({
                  '& > :not(th:first-of-type)': {
                    borderLeft: `1px solid ${theme.palette.grey[300]}`,
                  },
                }),
              },
              tableHeaderCell: {
                sx: (theme) => ({
                  ...theme.typography.body1Semibold,
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.grey[800],
                  p: theme.spacing(3),
                }),
              },
            }}
          />
          <TableBody
            sx={{
              '& > :not(tr:last-of-type) td': {
                borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
              },
            }}
          >
            {rows.map((row) => {
              const questions = row.question;

              return questions.map((question, index) => (
                <TableRow
                  key={`${row.id}-${question.qId}`}
                  sx={(theme) => ({
                    '& > td': {
                      p: theme.spacing(3),
                    },
                    '& > :not(td:first-of-type)': {
                      p: theme.spacing(2, 3),
                    },
                    backgroundColor: row.questionGroupUsage
                      ? theme.palette.grey[100]
                      : theme.palette.background.paper,
                  })}
                >
                  {index === 0 ? (
                    <>
                      <TableCell
                        rowSpan={questions.length}
                        sx={{
                          p: (theme) => theme.spacing(3),
                        }}
                      >
                        {row.questionGroupUsage ? (
                          <Checkbox checked disabled sx={{ p: (theme) => theme.spacing(0) }} />
                        ) : (
                          <FormCheckbox
                            name={name}
                            checked={value === row.id}
                            onChange={(_, checked) =>
                              formContext.setValue(name, checked ? row.id : undefined, {
                                shouldDirty: true,
                              })
                            }
                            sx={{ p: (theme) => theme.spacing(0) }}
                          />
                        )}
                      </TableCell>
                      <TableCell
                        rowSpan={questions.length}
                        sx={(theme) => ({
                          p: theme.spacing(3, 2),
                          borderLeft: `1px solid ${theme.palette.grey[300]}`,
                        })}
                      >
                        <Text variant='body2Semibold'>{row.name}</Text>
                      </TableCell>
                    </>
                  ) : null}

                  <TableCell
                    sx={(theme) => ({
                      p: theme.spacing(3, 2),
                      borderLeft: `1px solid ${theme.palette.grey[300]}`,
                    })}
                  >
                    <Text variant='body2Semibold'>{question.qId}</Text>
                  </TableCell>

                  <TableCell
                    sx={(theme) => ({
                      p: theme.spacing(3, 2),
                      borderLeft: `1px solid ${theme.palette.grey[300]}`,
                    })}
                  >
                    <DifficultyChip type={question.difficulty} />
                  </TableCell>

                  <TableCell
                    sx={(theme) => ({
                      p: theme.spacing(3, 2),
                      borderLeft: `1px solid ${theme.palette.grey[300]}`,
                    })}
                  >
                    <Chip
                      label={
                        <Text variant='captionSemibold'>
                          {question.calculated ? 'คำนวณ' : 'ไม่คำนวณ'}
                        </Text>
                      }
                      size='small'
                      sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}
                    />
                  </TableCell>

                  <TableCell
                    sx={(theme) => ({
                      p: theme.spacing(3, 2),
                      borderLeft: `1px solid ${theme.palette.grey[300]}`,
                    })}
                  >
                    <QuestionUsageStatus {...question.questionStatus} />
                  </TableCell>

                  <TableCell
                    align='center'
                    sx={(theme) => ({
                      p: theme.spacing(3, 2),
                      borderLeft: `1px solid ${theme.palette.grey[300]}`,
                    })}
                  >
                    {question.action}
                  </TableCell>
                </TableRow>
              ));
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {total === 0 ? overlayComponent : <></>}
    </Flex>
  );
}
